import { format, isSameDay, parseISO } from 'date-fns';
import { AvailableTime } from '../../types/AvailabilityModels';

export type DeleteOption =
  | 'this-occurrence'
  | 'this-time-and-topic-future'
  | 'today-this-topic'
  | 'all-future-this-topic';

export interface DeleteOptionDetail {
  option: DeleteOption;
  label: string;
  description: string;
  affectedIds: number[];
}

const topicKey = (slot: AvailableTime): string =>
  (slot.topics ?? [])
    .map((t) => t.id)
    .sort()
    .join(',');

const startTimeKey = (slot: AvailableTime): string =>
  format(parseISO(slot.startIso), 'HH:mm');

export const buildDeleteOptions = (
  target: AvailableTime,
  allSlots: AvailableTime[],
  bookedIds: Set<number> = new Set()
): DeleteOptionDetail[] => {
  const targetTopicKey = topicKey(target);
  const targetTimeKey = startTimeKey(target);
  const targetStart = parseISO(target.startIso).getTime();

  // "Future from target" = starts at or after the target slot, and not booked
  const isDeletable = (s: AvailableTime): boolean =>
    s.id != null &&
    !bookedIds.has(s.id as number) &&
    parseISO(s.startIso).getTime() >= targetStart;

  const fromTargetOnwards = allSlots.filter(isDeletable);

  // Option 1: just this slot (if not booked)
  const thisOccurrence =
    target.id != null && !bookedIds.has(target.id) ? [target.id] : [];

  // Option 2: from target onwards, same HH:mm start AND same topics
  const sameTimeSameTopicFuture = fromTargetOnwards
    .filter(
      (s) =>
        startTimeKey(s) === targetTimeKey &&
        topicKey(s) === targetTopicKey
    )
    .map((s) => s.id as number);

  // Option 3: slots on the same date with same topics, not booked
  const todaySameTopic = allSlots
    .filter(
      (s) =>
        s.id != null &&
        !bookedIds.has(s.id as number) &&
        isSameDay(parseISO(s.startIso), new Date(targetStart)) &&
        topicKey(s) === targetTopicKey
    )
    .map((s) => s.id as number);

  // Option 4: from target onwards, same topics
  const allFutureSameTopic = fromTargetOnwards
    .filter((s) => topicKey(s) === targetTopicKey)
    .map((s) => s.id as number);

  return [
    {
      option: 'this-occurrence',
      label: 'This occurrence only',
      description: 'Remove only this single slot',
      affectedIds: thisOccurrence,
    },
    {
      option: 'this-time-and-topic-future',
      label: 'This time and topic, every future date',
      description: 'Remove all future slots at the same time with this topic',
      affectedIds: sameTimeSameTopicFuture,
    },
    {
      option: 'today-this-topic',
      label: "Today's availability for this topic",
      description: 'Remove all slots on this date with this topic',
      affectedIds: todaySameTopic,
    },
    {
      option: 'all-future-this-topic',
      label: 'All future availability for this topic',
      description: 'Remove every future slot with this topic',
      affectedIds: allFutureSameTopic,
    },
  ];
};
